import React from "react";
import cn from "classnames";
import styles from "src/components/chart/shapes.module.css";
import sum from "lodash/sum";
import { AreaClosed } from "@visx/shape";
import { AreaStack } from "@visx/shape";
import { BarGroup } from "@visx/shape";
import { BarStack } from "@visx/shape";
import { ChartBounds } from "src/components/chart/bounds";
import { ChartLabelPie } from "src/components/chart/labels";
import { ChartLabelText } from "src/components/chart/labels";
import { ChartLabels } from "src/components/chart/labels";
import {
  ChartScale,
  MIN_SCATTER_CHART_CIRCLE_RADIUS,
  SCATTER_CHART_CIRCLE_OPACITY,
} from "src/components/chart/scales";
import { ChartScales } from "src/components/chart/scales";
import { ChartSeries } from "src/components/chart/series";
import { ChartSeriesTypes } from "src/components/chart/series";
import { ChartTooltipPie } from "src/components/chart/tooltip";
import { Circle } from "@visx/shape";
import { Group } from "@visx/group";
import { LinePath } from "@visx/shape";
import { Pie } from "@visx/shape";
import { QueryResultColumn } from "src/utils/results/types";
import { QueryResultRow } from "src/utils/results/types";
import { VisualChart } from "src/gql/visuals/types";
import { accessFloat } from "src/components/chart/scales";
import { accessString } from "src/components/chart/scales";
import { allFloats } from "src/components/chart/scales";
import { leftAxisColumns } from "src/components/chart/series";
import { chartOptionLabels } from "src/components/chart/options";
import { chartOptionStack } from "src/components/chart/options";
import { chartOptionVisible } from "src/components/chart/options";
import { fillEmptyRows } from "src/components/chart/scales";
import { formatValue } from "src/components/chart/format";
import { isBandScale } from "src/components/chart/scales";
import { nonEmptyRows } from "src/components/chart/scales";
import { positiveSums } from "src/components/chart/scales";
import { resultFloat } from "src/utils/results/values";
import { scaleAccessor } from "src/components/chart/scales";
import { scaleBand } from "@visx/scale";

interface ChartShapeProps {
  visual: VisualChart;
  series: ChartSeries;
  bounds: ChartBounds;
  scales: ChartScales;
  y: 0 | 1;
}

export const ChartShapeColumn: React.FC<ChartShapeProps> = (props) => {
  const ys = visibleColumns(props, shapeColumns(props, "column"));
  const y = shapeScaleY(props);

  if (ys.length === 0) {
    return null;
  }

  if (!isBandScale(props.scales.x) || isBandScale(y)) {
    return null;
  }

  const { rows } = props.series;
  const className = cn(styles.column, rows.length > 20 && styles.many);
  const y0 = y(shapeAxisLogarithmic(props) ? y.domain()[0] : 0);

  if (chartOptionStack(props.visual)) {
    return (
      <g className={cn(className, styles.stack)}>
        <BarStack
          keys={ys.map((y) => y.name)}
          data={fillEmptyRows(props.series.rows, ys)}
          x={accessString(props.series.x)}
          xScale={props.scales.x}
          yScale={y}
          color={props.scales.color}
          offset="diverging"
        >
          {(stacks) =>
            stacks.map((stack, i) =>
              stack.bars.map((b, j) => (
                <rect
                  key={`${i}-${j}`}
                  x={b.x}
                  y={b.y}
                  height={Math.abs(b.height)}
                  width={Math.abs(b.width)}
                  fill={b.color}
                />
              ))
            )
          }
        </BarStack>
      </g>
    );
  }

  const g = scaleBand({
    domain: ys.map((y) => y.name),
    range: [0, props.scales.x.bandwidth()],
  });

  return (
    <g className={className}>
      <BarGroup
        keys={ys.map((y) => y.name)}
        data={nonEmptyRows(props.series.rows, ys)}
        height={props.bounds.yMax}
        x0={accessString(props.series.x)}
        x0Scale={props.scales.x}
        x1Scale={g}
        yScale={y}
        color={props.scales.color}
      >
        {(groups) =>
          groups.map((group, i) => (
            <Group key={group.index} left={group.x0}>
              {group.bars.map((b, j) => (
                <React.Fragment key={`${i}-${j}`}>
                  <rect
                    x={b.x}
                    y={b.value < 0 ? y0 : b.y}
                    height={Math.abs(y(b.value ?? 0) - y0)}
                    width={Math.abs(b.width)}
                    fill={b.color}
                  />
                  {chartOptionLabels(props.visual) && (
                    <ChartLabelText x={b.x + b.width / 2} y={b.y - 10}>
                      {formatValue(
                        props.visual,
                        props.series,
                        ys.find((y) => y.name === b.key)!,
                        b.value,
                        { isRightYAxisSeries: props.y === 1 }
                      )}
                    </ChartLabelText>
                  )}
                </React.Fragment>
              ))}
            </Group>
          ))
        }
      </BarGroup>
    </g>
  );
};

export const ChartShapeArea: React.FC<ChartShapeProps> = (props) => {
  const ys = visibleColumns(props, shapeColumns(props, "area"));
  const y = shapeScaleY(props);

  if (ys.length === 0) {
    return null;
  }

  if (isBandScale(y)) {
    return null;
  }

  const isVisible = (column: QueryResultColumn) =>
    chartOptionVisible(props.visual, column.name);

  const hasNegativeNumbers =
    allFloats(
      props.series.rows,
      leftAxisColumns(props.series).filter(isVisible)
    ).filter((n) => n < 0).length > 0;

  if (chartOptionStack(props.visual)) {
    return (
      <g className={cn(styles.area, styles.stack)}>
        <AreaStack
          keys={ys.map((y) => y.name)}
          color={props.scales.color}
          data={fillEmptyRows(props.series.rows, ys)}
          offset={hasNegativeNumbers ? "diverging" : "none"}
          x={(d) => scaleAccessor(props.scales.x, props.series.x)(d.data)}
          y0={(d) => y(resultFloat(d[0]))}
          y1={(d) => y(resultFloat(d[1]))}
        />
      </g>
    );
  }

  // In this context, y0 and y1 DO NOT refer to left and right axis. From the docs:
  // The area generator produces an area, as in an area chart. An area is defined by two bounding lines.
  // Typically, the two lines share the same x-values (x0 = x1), differing only in y-value (y0 and y1).
  // The first line (the topline) is defined by x1 and y1 and is rendered first.
  // The second line (the baseline) is defined by x0 and y0 and is rendered second. Most commonly, y0 is defined as a constant representing zero.
  // So we define the baseline as 0, and then y1 is defined by the accessor, which is responsible for mapping the input data to a visual representation.
  return (
    <g className={styles.area}>
      {ys.map((c) => (
        <AreaClosed
          key={c.name}
          yScale={y}
          data={nonEmptyRows(props.series.rows, [c])}
          x={scaleAccessor(props.scales.x, props.series.x)}
          y0={y(0)}
          y1={scaleAccessor(y, c)}
          fill={props.scales.color(c.name)}
        />
      ))}
      {ys.map((c) => (
        <ChartLabels
          {...props}
          key={c.name}
          column={c}
          y={y}
          isRightYAxisSeries={props.y === 1}
        />
      ))}
    </g>
  );
};

export const ChartShapeLine: React.FC<ChartShapeProps> = (props) => {
  const ys = visibleColumns(props, shapeColumns(props, "line"));
  const y = shapeScaleY(props);

  if (ys.length === 0) {
    return null;
  }

  return (
    <g className={styles.line}>
      {ys.map((c) => (
        <LinePath
          key={c.name}
          data={nonEmptyRows(props.series.rows, [c])}
          x={scaleAccessor(props.scales.x, props.series.x)}
          y={scaleAccessor(y, c)}
          stroke={props.scales.color(c.name)}
        />
      ))}
      {ys.map((c) => (
        <ChartLabels
          {...props}
          key={c.name}
          column={c}
          y={y}
          isRightYAxisSeries={props.y === 1}
        />
      ))}
    </g>
  );
};

export const ChartShapeScatter: React.FC<ChartShapeProps> = (props) => {
  const ys = visibleColumns(props, shapeColumns(props, "scatter"));
  const y = shapeScaleY(props);
  if (ys.length === 0) {
    return null;
  }

  return (
    <g className={styles.line}>
      {ys.map((c, i) =>
        props.series.rows
          .filter((r) => c.name in r)
          .map((row, j) => (
            <Circle
              key={`${i}-${j}`}
              fill={props.scales.color(c.name)}
              {...getCircleProps(props, row, y, c)}
            />
          ))
      )}
      {ys.map((c) => (
        <ChartLabels
          {...props}
          key={c.name}
          column={c}
          y={y}
          isRightYAxisSeries={props.y === 1}
        />
      ))}
    </g>
  );
};

export const ChartShapePie: React.FC<ChartShapeProps> = (props) => {
  const ys = shapeColumns(props, "pie");

  const rows = React.useMemo(() => {
    return nonEmptyRows(props.series.rows, ys).filter((r) => {
      return chartOptionVisible(props.visual, String(r[props.series.x.name]));
    });
  }, [props.visual, props.series]);

  if (ys.length === 0) {
    return null;
  }

  const accessValue = (row: QueryResultRow) => {
    return accessFloat(ys[0])(row);
  };

  const accessColor = (row: QueryResultRow) => {
    return props.scales.color(accessString(props.series.x)(row));
  };

  const total = sum(positiveSums(rows, ys));
  const cx = props.bounds.xMax / 2;
  const cy = props.bounds.yMax / 2;
  const r1 = Math.min(cx, cy);
  const r0 = r1 / 2;

  // Add spacing between each pie slice. Do not add spacing for pies with
  // many slices as the spacing might render tiny slices invisible.
  const padAngle = rows.length > 100 ? 0 : 0.005;

  return (
    <Pie
      data={rows}
      pieValue={accessValue}
      outerRadius={r1}
      innerRadius={r0}
      padAngle={padAngle}
    >
      {({ arcs, path }) => (
        <Group top={cy} left={cx}>
          {arcs.map((a, i) => (
            <ChartTooltipPie {...props} row={a.data} key={i}>
              {({ onMouseMove, onMouseLeave }) => (
                <React.Fragment key={i}>
                  <path
                    d={path(a) ?? ""}
                    fill={accessColor(a.data)}
                    onMouseMove={onMouseMove}
                    onMouseLeave={onMouseLeave}
                  />
                  <ChartLabelPie
                    visual={props.visual}
                    column={ys[0]}
                    row={a.data}
                    total={total}
                    centroid={path.centroid(a)}
                  />
                </React.Fragment>
              )}
            </ChartTooltipPie>
          ))}
        </Group>
      )}
    </Pie>
  );
};

const shapeColumns = (props: ChartShapeProps, key: keyof ChartSeriesTypes) => {
  return props.y === 0 ? props.series.y0[key] : props.series.y1[key];
};

const shapeScaleY = (props: ChartShapeProps): ChartScale => {
  return props.y === 0 ? props.scales.y0! : props.scales.y1!;
};

const shapeAxisLogarithmic = (props: ChartShapeProps): boolean => {
  return props.visual.options.yAxis[props.y]?.type === "logarithmic";
};

const visibleColumns = (props: ChartShapeProps, cs: QueryResultColumn[]) => {
  return cs.filter((c) => chartOptionVisible(props.visual, c.name));
};

const getCircleProps = (
  props: ChartShapeProps,
  row: QueryResultRow,
  y: ChartScale,
  c: QueryResultColumn
) => {
  const {
    scales: { z: zScale },
    series: { z },
  } = props;
  let fillOpacity = 1;
  let radius = MIN_SCATTER_CHART_CIRCLE_RADIUS;
  const cx = scaleAccessor(props.scales.x, props.series.x)(row);
  const cy = scaleAccessor(y, c)(row);

  if (zScale) {
    fillOpacity = SCATTER_CHART_CIRCLE_OPACITY;
    radius = scaleAccessor(zScale, z)(row);
  }

  return {
    fillOpacity,
    r: radius,
    cx,
    cy,
  };
};
